<template>
  <div class="sales-section h-full">
    <div class="section-header">
      <h3 class="section-title"><i class="pi pi-chart-bar"></i> Venta del Mes</h3>
    </div>
    
    <div class="chart-wrapper flex flex-column justify-content-center relative" style="min-height: 250px;">
      
      <!-- Spinner de carga -->
      <div v-if="loading" class="flex align-items-center justify-content-center w-full absolute top-0 left-0 h-full bg-white-alpha-80 z-1">
        <ProgressSpinner style="width:50px;height:50px" />
      </div>

      <!-- Resumen Superior Numérico Dinámico -->
      <div v-if="tieneDatos" class="flex flex-wrap justify-content-center gap-4 mb-3 px-2">
        <div class="text-center">
          <span class="block text-500 text-sm font-semibold">TOTAL GENERAL</span>
          <span class="text-xl font-bold text-900">{{ totalPizzas }}</span>
        </div>
        <div v-for="(grupo, index) in porGrupo" :key="grupo.grupo_id" class="text-center">
          <span class="block text-sm font-semibold" :style="{ color: coloresHex[index % coloresHex.length] }">{{ grupo.grupo_nombre }}</span>
          <span class="text-xl font-bold text-900">
            {{ grupo.total }} <small class="text-xs text-500">({{ grupo.porcentaje }}%)</small>
          </span>
        </div>
      </div>

      <!-- Gráfico de Barras Apiladas -->
      <Chart 
        v-if="tieneDatos" 
        type="bar" 
        :data="chartData" 
        :options="chartOptions" 
        style="width: 100%; height: 250px;" 
      />

      <!-- Mensaje cuando no hay datos -->
      <div v-else-if="!loading" class="no-data w-full text-center p-5">
        <i class="pi pi-info-circle text-4xl text-500 mb-3"></i>
        <p class="m-0 text-600">No hay datos de producción para este rango</p>
      </div>

    </div>
  </div>
</template>

<script>
import ReporteService from '@/service/ReporteService';
import Chart from 'primevue/chart';
import ProgressSpinner from 'primevue/progressspinner';

export default {
  name: 'DashboardVentaMes',
  components: {
    Chart,
    ProgressSpinner
  },
  props: {
    fechaInicio: {
      type: [String, Date],
      required: true
    },
    fechaFin: {
      type: [String, Date],
      required: true
    },
    sucursalId: {
      type: [Number, String],
      default: null
    },
    grupoIds: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      loading: false,
      totalPizzas: 0,
      porGrupo: [],
      sucursalesData: [],
      chartData: {},
      coloresRGBA: [
        'rgba(59, 130, 246, 0.8)', // Azul
        'rgba(249, 115, 22, 0.8)', // Naranja
        'rgba(16, 185, 129, 0.8)', // Verde
        'rgba(139, 92, 246, 0.8)', // Violeta
        'rgba(236, 72, 153, 0.8)', // Rosa
        'rgba(234, 179, 8, 0.8)',  // Amarillo
      ],
      coloresHex: [
        '#3b82f6', // Azul
        '#f97316', // Naranja
        '#10b981', // Verde
        '#8b5cf6', // Violeta
        '#ec4899', // Rosa
        '#eab308', // Amarillo
      ],
      chartOptions: {
        indexAxis: 'y', // Cambia el gráfico a barras horizontales
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom'
          },
          tooltip: {
            mode: 'index',
            axis: 'y', // <-- Esta línea soluciona el problema de alineación horizontal
            intersect: false,
            callbacks: {
              label: function(context) {
                return ' ' + context.dataset.label + ': ' + context.raw;
              }
            }
          }
        },
        scales: {
          x: {
            beginAtZero: true,
            ticks: {
              precision: 0
            }
          },
          y: {
            // Se quita el stacked para que queden agrupadas una debajo de otra por sucursal
          }
        }
      }
    };
  },
  computed: {
    tieneDatos() {
      return this.chartData && this.chartData.datasets && this.chartData.datasets.length > 0;
    }
  },
  watch: {
    fechaInicio: 'cargarDatos',
    fechaFin: 'cargarDatos',
    sucursalId: 'cargarDatos',
    grupoIds: {
      handler: 'cargarDatos',
      deep: true
    }
  },
  created() {
    this.reporteService = new ReporteService();
    // Definimos el footer dinámicamente para tener el contexto de "this" correcto
    this.chartOptions.plugins.tooltip.callbacks.footer = (tooltipItems) => {
      if (!tooltipItems || tooltipItems.length === 0) return '';
      const index = tooltipItems[0].dataIndex;
      if (this.sucursalesData && this.sucursalesData[index]) {
        return 'Total general: ' + this.sucursalesData[index].total + ' pizzas';
      }
      return '';
    };
  },
  mounted() {
    this.cargarDatos();
  },
  methods: {
    async cargarDatos() {
      if (!this.fechaInicio || !this.fechaFin) return;
      
      this.loading = true;
      try {
        const fechaIniStr = typeof this.fechaInicio === 'string' ? this.fechaInicio : this.fechaInicio.toISOString().split('T')[0] + ' 00:00:00';
        const fechaFinStr = typeof this.fechaFin === 'string' ? this.fechaFin : this.fechaFin.toISOString().split('T')[0] + ' 23:59:59';
        
        const params = {
          fecha_inicio: fechaIniStr,
          fecha_fin: fechaFinStr
        };

        if (this.sucursalId) params.sucursal_id = this.sucursalId;
        if (this.grupoIds && this.grupoIds.length > 0) params.grupo_ids = this.grupoIds;

        const res = await this.reporteService.getVentasDelMes(params);
        
        if (res && res.sucursales && res.sucursales.length > 0) {
          this.totalPizzas = res.total_general || 0;
          this.porGrupo = res.por_grupo || [];
          this.sucursalesData = res.sucursales; // Almacenamos para poder leerlo desde el tooltip

          // Construir datasets dinámicos basados en los grupos que llegaron
          const datasetsDinamicos = this.porGrupo.map((grupo, index) => {
            return {
              label: grupo.grupo_nombre,
              backgroundColor: this.coloresRGBA[index % this.coloresRGBA.length],
              borderColor: this.coloresHex[index % this.coloresHex.length],
              borderWidth: 1,
              // Buscamos la cantidad de este grupo dentro de cada sucursal
              data: res.sucursales.map(s => {
                return s.grupos && s.grupos[grupo.grupo_nombre] ? s.grupos[grupo.grupo_nombre] : 0;
              })
            };
          });

          // Armamos la gráfica
          this.chartData = {
            labels: res.sucursales.map(s => s.sucursal),
            datasets: datasetsDinamicos
          };
        } else {
          this.chartData = {};
          this.porGrupo = [];
          this.totalPizzas = 0;
        }
      } catch (error) {
        console.error('Error al cargar ventas del mes:', error);
        this.chartData = {};
        this.porGrupo = [];
        this.totalPizzas = 0;
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.sales-section {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}
.section-title {
  margin: 0 0 1rem 0;
  color: #2c3e50;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  font-weight: bold;
  gap: 0.5rem;
}
.no-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
</style>